import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { LockIcon } from '../components/icons';
import { ErrorText, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import type { EmailGate, SharePayload, VideoSettings } from '../lib/types';
import { qk } from '../queries/keys';
import { updateVideoSettings } from '../queries/share';
import { fetchVideoDefaults, type VideoSettingKey } from '../queries/videoDefaults';
import { PrivacyControl } from './PrivacyControl';

/**
 * The owner's controls for one recording, on its own page. Every switch
 * writes straight through; the payload in the cache is patched so the page
 * reflects the change without a refetch.
 *
 * A setting the owner's team has locked is shown as theirs and left alone
 * here. The API refuses it either way, but being told no after clicking is a
 * poor way to learn that your team has already decided.
 */
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

export function ShareSettings({
  videoId,
  shareId,
  settings,
}: {
  videoId: string;
  shareId: string;
  settings: VideoSettings;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // The same defaults the Settings page shows: what the team has locked, and
  // its name, for the line that says whose answer it is.
  const defaults = useQuery({ queryKey: qk.videoDefaults(), queryFn: fetchVideoDefaults });
  const locked = (key: VideoSettingKey) => (defaults.data?.locked ?? []).includes(key);
  const teamName = defaults.data?.team?.name ?? null;

  const save = useMutation({
    mutationFn: (patch: Partial<VideoSettings>) => updateVideoSettings(videoId, patch),
    onMutate: (patch) => {
      setError(null);
      // Every share query for this recording, whoever is asking.
      queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(shareId) }, (current) =>
        current?.settings ? { ...current, settings: { ...current.settings, ...patch } } : current,
      );
    },
    onSuccess: (next, patch) => {
      for (const [setting, value] of Object.entries(patch)) {
        track('Share Settings Changed', { video_id: shareId, setting, value });
      }
      queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(shareId) }, (current) =>
        current
          ? {
              ...current,
              settings: next,
              viewer: current.viewer
                ? {
                    ...current.viewer,
                    captionsOn: next.captionsOn,
                    playbackRate: next.playbackRate,
                    commentsEnabled: next.commentsEnabled,
                    reactionsEnabled: next.reactionsEnabled,
                  }
                : current.viewer,
            }
          : current,
      );
    },
    onError: (cause) => {
      setError(errorMessage(cause));
      void queryClient.invalidateQueries({ queryKey: qk.share(shareId) });
    },
  });

  const flip = (key: keyof VideoSettings) => () =>
    save.mutate({ [key]: !settings[key] } as Partial<VideoSettings>);

  return (
    <div>
      <PrivacyControl
        videoId={videoId}
        visibility={settings.visibility}
        teamName={teamName}
        disabled={locked('visibility')}
        onChange={(visibility) => save.mutate({ visibility })}
      />
      <Row title="Turn on captions" detail="Captions show for viewers without them asking" locked={locked('captionsOn')} teamName={teamName}>
        <Switch on={settings.captionsOn} disabled={locked('captionsOn')} onChange={flip('captionsOn')} label="Turn on captions" />
      </Row>
      <Row title="Recommend a playback speed" detail="The speed the page opens at" locked={locked('playbackRate')} teamName={teamName}>
        <select
          value={settings.playbackRate}
          disabled={locked('playbackRate')}
          onChange={(event) => save.mutate({ playbackRate: Number(event.target.value) })}
          aria-label="Recommended playback speed"
          className="h-8 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 font-mono text-caption text-chalk disabled:opacity-40"
        >
          {SPEEDS.map((speed) => (
            <option key={speed} value={speed}>
              {speed}×
            </option>
          ))}
        </select>
      </Row>
      <Row
        title="Request email to view"
        detail="Viewers who are not signed in give an email before watching"
        locked={locked('emailGate')}
        teamName={teamName}
      >
        <select
          value={settings.emailGate}
          disabled={locked('emailGate')}
          onChange={(event) => save.mutate({ emailGate: event.target.value as EmailGate })}
          aria-label="Request email to view"
          className="h-8 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk disabled:opacity-40"
        >
          <option value="none">No</option>
          <option value="ask">Ask for email</option>
          <option value="require">Require email</option>
        </select>
      </Row>
      <Row title="Comments" detail="Viewers can add comments" locked={locked('commentsEnabled')} teamName={teamName}>
        <Switch on={settings.commentsEnabled} disabled={locked('commentsEnabled')} onChange={flip('commentsEnabled')} label="Comments" />
      </Row>
      <Row title="Emoji reactions" detail="Viewers can react at a moment" locked={locked('reactionsEnabled')} teamName={teamName}>
        <Switch on={settings.reactionsEnabled} disabled={locked('reactionsEnabled')} onChange={flip('reactionsEnabled')} label="Emoji reactions" />
      </Row>
      <Row title="Download" detail="Viewers can download the video" locked={locked('downloadEnabled')} teamName={teamName}>
        <Switch on={settings.downloadEnabled} disabled={locked('downloadEnabled')} onChange={flip('downloadEnabled')} label="Download" />
      </Row>
      <Row title="Analytics" detail="Viewers can see the view count" locked={locked('analyticsPublic')} teamName={teamName}>
        <Switch on={settings.analyticsPublic} disabled={locked('analyticsPublic')} onChange={flip('analyticsPublic')} label="Analytics" />
      </Row>
      <Row title="Transcript" detail="Viewers get the transcript and captions" locked={locked('transcriptPublic')} teamName={teamName}>
        <Switch on={settings.transcriptPublic} disabled={locked('transcriptPublic')} onChange={flip('transcriptPublic')} label="Transcript" />
      </Row>
      <Row title="Links at the end" detail="Show the pages you showed only after the video has played" last locked={locked('linksAtEnd')} teamName={teamName}>
        <Switch on={settings.linksAtEnd} disabled={locked('linksAtEnd')} onChange={flip('linksAtEnd')} label="Links at the end" />
      </Row>
      {error ? (
        <div className="mt-3">
          <ErrorText>{error}</ErrorText>
        </div>
      ) : null}
    </div>
  );
}

function Row({
  title,
  detail,
  last,
  locked,
  teamName,
  children,
}: {
  title: string;
  detail: string;
  last?: boolean;
  /** The owner's team answers this one; the control is theirs to look at only. */
  locked?: boolean;
  teamName?: string | null;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        'flex items-center justify-between gap-4 py-3.5',
        !last && 'border-b border-hairline',
      )}
    >
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-body text-chalk">
          {title}
          {locked ? <LockIcon size={12} className="shrink-0 text-signal" /> : null}
        </p>
        <p className="mt-0.5 text-caption text-chalk-faint">
          {locked ? `${teamName ?? 'Your team'} sets this for everyone` : detail}
        </p>
      </div>
      {children}
    </div>
  );
}

function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cx(
        'relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-120',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/60',
        'disabled:pointer-events-none disabled:opacity-40',
        on ? 'border-go bg-go' : 'border-slate-600 bg-slate-800',
      )}
    >
      <span
        className={cx(
          'absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full shadow-sm transition-transform duration-120',
          on ? 'translate-x-4 bg-white' : 'translate-x-0 bg-chalk-dim',
        )}
      />
    </button>
  );
}
